import { Segment, LineSegment } from "./segment";
import { Pt, pt } from "./pt";
import { Chain } from "./chain";
import { intersections } from "./intersections";
import { split } from "./split";
import { makeChains } from "./graph";

enum Location {
  Inside,
  Outside,
  SameEdge,
  DiffEdge,
}

interface InsideResult {
  inside: boolean;
  segment: Segment;
}

abstract class Operation {
  keep: Chain[];
  drop: Chain[];

  constructor(aChains: Chain[], bChains: Chain[]) {
    let { split1, split2, arcs1, arcs2 } = split(aChains, bChains);
    if (this.reverse2()) {
      arcs2 = arcs2.map((s) => s.reverse());
      arcs2.reverse();
    }
    const keep1 = arcs1.filter((a) =>
      this.keep1Inside2(chainLocation(a, split2))
    );
    const keep2 = arcs2.filter((a) =>
      this.keep2Inside1(chainLocation(a, split1))
    );
    this.keep = keep1.concat(keep2);
    this.drop = arcs1.concat(arcs2).filter((a) => !this.keep.includes(a));
  }

  get chains(): Chain[] {
    return makeChains(this.keep);
  }

  abstract keep1Inside2(p: Location): boolean;
  abstract keep2Inside1(p: Location): boolean;
  abstract reverse2(): boolean;
}

class Union extends Operation {
  keep1Inside2(p: Location): boolean {
    return p == Location.Outside || p == Location.SameEdge;
  }

  keep2Inside1(p: Location): boolean {
    return p == Location.Outside;
  }

  reverse2() {
    return false;
  }
}

class Intersection extends Operation {
  keep1Inside2(p: Location): boolean {
    return p == Location.Inside || p == Location.SameEdge;
  }

  keep2Inside1(p: Location): boolean {
    return p == Location.Inside;
  }

  reverse2() {
    return false;
  }
}

class Difference extends Operation {
  keep1Inside2(p: Location): boolean {
    return p == Location.Outside || p == Location.DiffEdge;
  }

  keep2Inside1(p: Location): boolean {
    return p == Location.Inside;
  }

  reverse2() {
    return true;
  }
}

function isTop(segment: Segment): boolean {
  if (segment == null) {
    return false;
  }

  const dx = segment.end.x - segment.start.x;
  if (dx == 0) {
    return segment.end.y > segment.start.y;
  } else {
    return dx < 0;
  }
}

function chainLocation(chain: Chain, chains: Chain[]): Location {
  const p = chain.position(1.0 / Math.E);
  const result = insideChains(chains, p);

  if (!result.inside) {
    return Location.Outside;
  } else if (result.segment == null) {
    return Location.Inside;
  } else if (isTop(chain.segments[0]) == isTop(result.segment)) {
    return Location.SameEdge;
  } else {
    return Location.DiffEdge;
  }
}

function insideChains(chains: Chain[], p: Pt): InsideResult {
  for (let chain of chains) {
    const result = inside(chain, p);
    if (result.inside) {
      if (result.segment == null && chain.isClockwise) {
        return { inside: true, segment: null };
      } else {
        return result;
      }
    }
  }
  return { inside: false, segment: null };
}

function inside(chain: Chain, pt1: Pt): InsideResult {
  let bounds = chain.bounds;
  if (
    pt1.x < bounds.lowX ||
    pt1.x > bounds.highX ||
    pt1.y < bounds.lowY ||
    pt1.y > bounds.highY
  )
    return { inside: false, segment: null };

  var count = 0;
  let ray = new LineSegment(pt1, pt(bounds.highX + 1, pt1.y));
  chain.segments.forEach((s) => {
    let b = s.bounds;
    let flat = b.lowY == b.highY;

    if (pt1.x < b.lowX) {
      if (pt1.y >= b.lowY && pt1.y < b.highY) {
        count++;
      }
    } else if (
      pt1.x >= b.lowX &&
      pt1.x <= b.highX &&
      pt1.y >= b.lowY &&
      pt1.y <= b.highY
    ) {
      let ips = intersections(ray, s);
      if (ips.length > 0) {
        if (ips.some((ip) => ip.segment == ray && ip.pos == 0))
          return { inside: true, segment: s };
        else if (!flat && pt1.y < b.highY) count++;
      }
    }
  });

  if (count % 2 == 1) return { inside: true, segment: null };
  else return { inside: false, segment: null };
}

export { Union, Intersection, Difference };
